<template>
    <transition-group name="cz3__nested-panel">
        <step-options-nested-panel-wrapper
            v-for="panel in renderableNestedPanels"
            :key="panel.key"
            :z-index="zIndexForIndex(panel.stackIndex)"
        >
            <focus-lock :disabled="hasOpenModal || !hasOpenNestedPanel" group="nested-panel"
                class="content-focus-lock flex flex-col flex-1 min-h-0"
            >
                <component
                    :is="panel.component"
                    v-bind="panel.props"
                />
            </focus-lock>
        </step-options-nested-panel-wrapper>
    </transition-group>
</template>

<script>
    import FocusLock from 'vue-focus-lock';
    import allStores from '../stores/all';
    import StepOptionsNestedPanelWrapper from './StepOptionsNestedPanelWrapper.vue';
    import StepOptionsZoomIn from './StepOptionsZoomIn.vue';
    import ColorChangeConfirm from './ColorChangeConfirm.vue';
    import AllColorLocations from './AllColorLocations.vue';
    import PreviewPanel from './PreviewPanel.vue';
    import StepOptionsCharactersInitialsOptions from './StepOptionsCharactersInitialsOptions.vue';
    import StepOptionsFrontPaintfillOptions from './StepOptionsFrontPaintfillOptions.vue';
    import StepOptionsFrontStampsOptions from './StepOptionsFrontStampsOptions.vue';

    export default {
        name: 'StepOptionsNestedPanels',

        mixins: [allStores],

        components: {
            FocusLock,
            StepOptionsNestedPanelWrapper,
            StepOptionsZoomIn,
            ColorChangeConfirm,
            AllColorLocations,
            PreviewPanel,
            StepOptionsCharactersInitialsOptions,
            StepOptionsFrontPaintfillOptions,
            StepOptionsFrontStampsOptions,
        },

        props: {
            isDirty: {
                type: Boolean,
                default: false,
            },
        },

        computed: {
            nestedPanels() {
                return this.appStore.nestedPanels;
            },

            hasOpenNestedPanel() {
                return this.appStore.hasOpenNestedPanel;
            },

            hasOpenModal() {
                return this.appStore.hasOpenModal;
            },

            activeNestedPanel() {
                return this.appStore.activeNestedPanel;
            },

            visibleGroups() {
                return this.customizerStore.visibleGroups;
            },

            shaftBandGroupData() {
                return this.visibleGroups.find((group) => group.code === 'PutterShaftBand');
            },

            hasShaftBandData() {
                return Array.isArray(this.shaftBandGroupData?.placements)
                    && this.shaftBandGroupData.placements.length > 0;
            },

            panelRegistry() {
                return {
                    zoomIn: {
                        canRender: true,
                        component: StepOptionsZoomIn,
                        getProps: (panel) => ({
                            isDirty: this.isDirty,
                            payload: panel.payload,
                        }),
                    },
                    colorChangeConfirm: {
                        canRender: true,
                        component: ColorChangeConfirm,
                        getProps: (panel) => ({
                            payload: panel.payload,
                        }),
                    },
                    allColorLocations: {
                        canRender: true,
                        component: AllColorLocations,
                        getProps: (panel) => ({
                            payload: panel.payload,
                        }),
                    },
                    'preview-panel': {
                        canRender: true,
                        component: PreviewPanel,
                        getProps: (panel) => ({
                            ...(panel.payload || {}),
                        }),
                    },
                    charactersInitialsOptions: {
                        canRender: true,
                        component: StepOptionsCharactersInitialsOptions,
                        getProps: (panel) => ({
                            ...(panel.payload || {}),
                        }),
                    },
                    frontStampsOptions: {
                        canRender: true,
                        component: StepOptionsFrontStampsOptions,
                        getProps: (panel) => ({
                            ...(panel.payload || {}),
                        }),
                    },
                    frontPaintfillOptions: {
                        canRender: true,
                        component: StepOptionsFrontPaintfillOptions,
                        getProps: (panel) => ({
                            ...(panel.payload || {}),
                        }),
                    },
                };
            },

            renderableNestedPanels() {
                return this.nestedPanels
                    .map((panel, stackIndex) => {
                        const config = this.getPanelConfig(panel);

                        if (!config) {
                            return null;
                        }

                        return {
                            key: `${stackIndex}-${panel.panelId}`,
                            stackIndex,
                            panelId: panel.panelId,
                            component: config.component,
                            props: config.getProps(panel),
                        };
                    })
                    .filter(Boolean);
            },
        },

        watch: {
            activeNestedPanel(panel) {
                if (panel && !this.getPanelConfig(panel)) {
                    this.appStore.closeNestedPanel();
                }
            },
        },

        methods: {
            getPanelConfig(panel) {
                if (!panel?.panelId) {
                    return null;
                }

                const config = this.panelRegistry[panel.panelId];

                if (!config || !config.canRender) {
                    return null;
                }

                return config;
            },

            zIndexForIndex(index) {
                return 70 + (index * 10);
            },
        },
    };
</script>

<style>
    .cz3__nested-panel-enter-active,
    .cz3__nested-panel-leave-active {
        transition: transform 500ms ease-in-out;
        will-change: transform;
    }

    .cz3__nested-panel-enter-from,
    .cz3__nested-panel-leave-to {
        transform: translateX(1124px);
    }

    .cz3__nested-panel-enter-to,
    .cz3__nested-panel-leave-from {
        transform: translateX(0);
    }

    @media (min-width: 1024px) {
        .cz3__nested-panel-enter-from,
        .cz3__nested-panel-leave-to {
            transform: translateX(650px);
        }
    }

    #cz3 .cz3__nested-options-focus-lock > [data-lock] {
        display: flex;
        flex: 1;
        flex-direction: column;
        height: 100%;
        min-height: 0;
    }
</style>
